/**
 * GitHub Issues API를 통한 피드백 관리 시스템
 * Zero-Risk 접근: 기존 localStorage 시스템은 건드리지 않고 추가 기능으로만 동작
 */

class GitHubIssuesManager {
  constructor() {
    // GitHub 설정 (환경변수에서 로드)
    this.token = process.env.REACT_APP_GITHUB_TOKEN;
    this.owner = process.env.REACT_APP_GITHUB_OWNER || 'your-username';
    this.repo = process.env.REACT_APP_GITHUB_REPO || 'your-repo';
    this.baseUrl = 'https://api.github.com';
    
    // GitHub API 사용 가능 여부 체크
    this.isEnabled = !!(this.token && this.owner && this.repo);
    
    if (!this.isEnabled) {
      console.log('GitHub Issues 연동이 비활성화됨 - 환경변수 확인 필요');
    }
  }

  /**
   * 피드백 유형별 라벨 매핑
   */
  getLabelsByCategory(category, mood, priority = 'medium') {
    const baseLabels = ['feedback'];
    
    // 카테고리별 라벨
    const categoryLabels = {
      'performance': ['성능', 'bug'],
      'ui': ['UI/UX', 'enhancement'],
      'feature': ['기능요청', 'enhancement'],
      'content': ['콘텐츠', 'enhancement'],
      'issues': ['버그', 'bug'],
      'events': ['사용성', 'enhancement']
    };

    // 감정별 라벨
    const moodLabels = {
      'positive': ['😊 긍정적'],
      'neutral': ['😐 중립적'], 
      'negative': ['😞 부정적']
    };

    // 우선순위별 라벨
    const priorityLabels = {
      'critical': ['🚨 긴급'],
      'high': ['⚡ 높음'],
      'medium': ['📋 보통'],
      'low': ['📝 낮음']
    };

    return [
      ...baseLabels,
      ...(categoryLabels[category] || []),
      ...(moodLabels[mood] || []),
      ...(priorityLabels[priority] || [])
    ];
  }

  /**
   * 피드백 데이터를 GitHub Issue로 변환
   */
  formatFeedbackAsIssue(feedback) {
    const timestamp = new Date(feedback.timestamp).toLocaleString('ko-KR');
    const labels = this.getLabelsByCategory(feedback.category, feedback.mood, feedback.priority);
    
    // 이슈 제목 생성
    const titleMap = {
      'performance': '성능 피드백',
      'ui': 'UI/UX 피드백',
      'feature': '기능 요청',
      'content': '콘텐츠 피드백',
      'issues': '문제 신고',
      'events': '사용성 피드백'
    };
    
    const title = `[${titleMap[feedback.category] || '피드백'}] ${feedback.message.substring(0, 50)}${feedback.message.length > 50 ? '...' : ''}`;

    // 이슈 본문 생성
    const body = `
## 📝 피드백 내용
${feedback.message}

## 📊 상세 정보
- **카테고리**: ${feedback.category}
- **감정**: ${feedback.mood} ${this.getMoodEmoji(feedback.mood)}
- **우선순위**: ${feedback.priority}
- **트리거**: ${feedback.triggerName || '수동'}
- **발생 시간**: ${timestamp}

## 🔍 컨텍스트 정보
${feedback.context ? '```json\n' + JSON.stringify(feedback.context, null, 2) + '\n```' : '추가 컨텍스트 정보가 없습니다.'}

## 🎯 추가 정보
- **이탈 의도**: ${feedback.isExitIntent ? '예' : '아니오'}
- **세션 ID**: ${feedback.sessionId || 'N/A'}
- **사용자 ID**: ${feedback.userId || '익명'}

---
*이 이슈는 자동으로 생성되었습니다. | 생성 시간: ${new Date().toISOString()}*
    `.trim();

    return {
      title,
      body,
      labels
    };
  }

  /**
   * 감정별 이모지 반환
   */
  getMoodEmoji(mood) {
    const emojiMap = {
      'positive': '😊',
      'neutral': '😐',
      'negative': '😞'
    };
    return emojiMap[mood] || '🤔';
  }

  /**
   * GitHub Issues API 호출
   */
  async apiCall(endpoint, method = 'GET', data = null) {
    if (!this.isEnabled) {
      throw new Error('GitHub Issues API가 설정되지 않았습니다.');
    }

    const url = `${this.baseUrl}${endpoint}`;
    const options = {
      method,
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Accept': 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
        'Content-Type': 'application/json'
      }
    };

    if (data && (method === 'POST' || method === 'PATCH')) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('GitHub API 오류 상세 정보:', {
        status: response.status,
        statusText: response.statusText,
        endpoint,
        errorData
      });
      throw new Error(`GitHub API 오류: ${response.status} - ${errorData.message || response.statusText}`);
    }

    return response.json();
  }

  /**
   * 피드백을 GitHub Issue로 생성
   */
  async createIssue(feedback) {
    try {
      if (!this.isEnabled) {
        console.log('GitHub Issues 비활성화 상태 - localStorage로만 저장됨');
        return null;
      }

      const issueData = this.formatFeedbackAsIssue(feedback);
      const endpoint = `/repos/${this.owner}/${this.repo}/issues`;
      
      const result = await this.apiCall(endpoint, 'POST', issueData);
      
      console.log('GitHub Issue 생성 성공:', result.html_url);
      return {
        success: true,
        issueNumber: result.number,
        issueUrl: result.html_url,
        issueId: result.id
      };

    } catch (error) {
      console.error('GitHub Issue 생성 실패:', error.message);
      
      // 실패해도 기존 시스템에는 영향 없음 (Zero-Risk)
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * 레포지토리의 피드백 이슈들 조회
   */
  async getFeedbackIssues(page = 1, perPage = 30) {
    try {
      if (!this.isEnabled) {
        return { issues: [], total: 0, hasMore: false };
      }

      const endpoint = `/repos/${this.owner}/${this.repo}/issues`;
      const params = new URLSearchParams({
        labels: 'feedback',
        state: 'all',
        sort: 'created',
        direction: 'desc',
        page: page.toString(),
        per_page: perPage.toString()
      });

      const issues = await this.apiCall(`${endpoint}?${params}`);
      
      return {
        issues: issues.map(issue => ({
          id: issue.id,
          number: issue.number,
          title: issue.title,
          body: issue.body,
          state: issue.state,
          labels: issue.labels.map(label => label.name),
          createdAt: issue.created_at,
          updatedAt: issue.updated_at,
          url: issue.html_url,
          user: issue.user ? issue.user.login : 'Unknown'
        })),
        total: issues.length,
        hasMore: issues.length === perPage
      };

    } catch (error) {
      console.error('GitHub Issues 조회 실패:', error.message);
      return { issues: [], total: 0, hasMore: false, error: error.message };
    }
  }

  /**
   * 특정 이슈 상태 업데이트
   */
  async updateIssueState(issueNumber, state = 'closed') {
    try {
      if (!this.isEnabled) return null;

      const endpoint = `/repos/${this.owner}/${this.repo}/issues/${issueNumber}`;
      const result = await this.apiCall(endpoint, 'PATCH', { state });
      
      return {
        success: true,
        issueNumber: result.number,
        newState: result.state
      };

    } catch (error) {
      console.error('이슈 상태 업데이트 실패:', error.message);
      return { success: false, error: error.message };
    }
  }

  /**
   * 이슈에 댓글 추가
   */
  async addComment(issueNumber, comment) {
    try {
      if (!this.isEnabled) return null;

      const endpoint = `/repos/${this.owner}/${this.repo}/issues/${issueNumber}/comments`;
      const result = await this.apiCall(endpoint, 'POST', { body: comment });
      
      return {
        success: true,
        commentId: result.id,
        commentUrl: result.html_url
      };

    } catch (error) {
      console.error('댓글 추가 실패:', error.message);
      return { success: false, error: error.message };
    }
  }

  /**
   * GitHub 연동 상태 확인
   */
  async checkConnection() {
    try {
      if (!this.isEnabled) {
        return { 
          connected: false, 
          message: '환경변수가 설정되지 않았습니다.',
          config: {
            hasToken: !!this.token,
            hasOwner: !!this.owner,
            hasRepo: !!this.repo
          }
        };
      }

      // 레포지토리 접근 테스트
      const endpoint = `/repos/${this.owner}/${this.repo}`;
      const repo = await this.apiCall(endpoint);
      
      return {
        connected: true,
        message: 'GitHub Issues 연동이 정상적으로 작동합니다.',
        repoInfo: {
          name: repo.name,
          fullName: repo.full_name,
          private: repo.private,
          openIssues: repo.open_issues_count
        }
      };

    } catch (error) {
      return {
        connected: false,
        message: `연결 실패: ${error.message}`,
        error: error.message
      };
    }
  }
}

// 싱글톤 인스턴스 생성
export const githubIssues = new GitHubIssuesManager();

/**
 * 피드백을 GitHub Issue로 전송하는 함수 (기존 시스템에 추가)
 */
export const sendFeedbackToGitHub = async (feedback) => {
  try {
    // GitHub으로 전송 시도
    const result = await githubIssues.createIssue(feedback);
    
    if (result && result.success) {
      console.log(`✅ 피드백이 GitHub Issue #${result.issueNumber}로 생성되었습니다.`);
      
      // 성공 시 localStorage에 GitHub 정보 추가로 저장
      const existingFeedback = JSON.parse(localStorage.getItem('autoFeedback') || '[]');
      const updatedFeedback = existingFeedback.map(item => 
        item.timestamp === feedback.timestamp 
          ? { ...item, githubIssue: result }
          : item
      );
      localStorage.setItem('autoFeedback', JSON.stringify(updatedFeedback));
    } else {
      console.log('⚠️ GitHub Issue 생성 실패, localStorage에만 저장됨');
    }
    
    return result;
    
  } catch (error) {
    console.error('GitHub 전송 중 오류 발생:', error);
    // 실패해도 기존 시스템에는 영향 없음
    return { success: false, error: error.message };
  }
};

export default githubIssues;